import { useAssistantRuntime } from '@assistant-ui/react';
import { NavList } from '@primer/react';
import { ResourcelessPermissionId } from '@upright/common/iam/privilege/permission/types';
import { isDmaConsultantUser } from '@upright/common/utils';
import { useHasResourcelessPrivilege } from '@upright/ui/api/iam/privilege/query-options';
import { startNewChat } from '@upright/ui/chat/history/chat-history-actions';
import {
  type ChatConversationListItem,
  conversationDisplayTitle,
  useChatConversationList,
} from '@upright/ui/chat/history/chat-history-data';
import { ChatDeleteDialog } from '@upright/ui/chat/history/chat-delete-dialog';
import { useActiveConversationId, useChatConversationListSync } from '@upright/ui/chat/history/chat-history-hooks';
import { useAskAiEntryPoint } from '@upright/ui/chat/use-ask-ai-entry-point';
import { LinkWithSearch } from '@upright/ui/common/generic/components/navigation/link-with-search';
import {
  CUSTOM_COMPANIES_PATH,
  FAVORITES_PATH,
  FUNDS_AND_PORTFOLIOS_PATH,
  MANAGE_ACCESS_PATH,
  SCREEN_COMPANIES_PATH,
  SCREEN_FUNDS_PATH,
} from '@upright/ui/common/generic/components/navigation/page-paths';
import { Tooltip } from '@upright/ui/common/generic/components/tooltip';
import { useGetLoggedInUser, useIsTeam } from '@upright/ui/common/generic/hooks';
import {
  DeleteSteamlineCore,
  HomeChimney,
  OfficeWorkerStreamlineCore,
  Plus,
  Star1StreamlineCore,
} from '@upright/ui/icons';
import AiSparklesIcon from '@upright/ui/icons/ai-sparkles-streamline-core';
import BagSuitcaseIcon from '@upright/ui/icons/bag-suitcase-1-streamline-core';
import BuildingOfficeIcon from '@upright/ui/icons/building-office-streamline-core';
import HistoryIcon from '@upright/ui/icons/fastforward-clock-streamline-core';
import InterfaceSearch from '@upright/ui/icons/img/streamline-mini-line/interface-essential/search/interface-search.svg';
import SearchDollarIcon from '@upright/ui/icons/search-dollar-streamline-core';
import ZoomDocumentIcon from '@upright/ui/icons/zoom-document-streamline-core';
import { useOrganizationHasCreateCompaniesPermission } from '@upright/ui/views/home-views/custom-companies-view/hooks/use-organization-has-create-companies-permission';
import { showSharedAssets, useFrontPageConfig } from '@upright/ui/views/home-views/front-page-config';
import { OrganizationSelectorSidebar } from '@upright/ui/views/home-views/organizations-assets-view/organization-selector-sidebar';
import { useSelectedOrganization } from '@upright/ui/views/home-views/selected-organization-context';
import {
  getAriaCurrent,
  NavSectionAction,
  NavSectionEmptyMessage,
  NavSectionTitle,
  StyledNavList,
  SubNavItem,
  TopLevelItemWrap,
} from '@upright/ui/views/shared/navigation/styled-nav-components';
import { useNavSectionCollapse } from '@upright/ui/views/shared/navigation/use-nav-section-collapse';
import { useState, type ComponentType, type MouseEvent as ReactMouseEvent, type ReactNode, type SVGProps } from 'react';
import { useLocation } from 'react-router-dom';
import { sharedAssetsLabel } from '@upright/ui/views/user-view/self-serve-labels';
import { useHasSelfServeAccess } from '@upright/ui/views/user-view/use-self-serve-access';
import { OrganizationRailRow } from './organization-rail-row';
import {
  ALL_CHATS_PATH,
  CHAT_PATH,
  CHAT_SEARCH_PARAM,
  chatConversationPath,
  FOCUS_CHAT_SEARCH_STATE,
  isChatPath,
} from './unified-shell-chat-routes';
import styles from './unified-investor-navigation.module.css';

// Share the styling with the story, not a wrapper component: Primer recognizes
// NavList.TrailingAction by component identity when separating it from the row link.
export const chatDeleteClassName = styles.chatDelete;
// The rail's own class, which the row label size and the delete control's overlay depend on.
export const railClassName = styles.rail;

/**
 * The unified shell's investor rail (PLT-6528): the organisation selector, a Home row, then
 * **titled, collapsible** groups — Chats, My assets, Database.
 *
 * It is a second component rather than an edit to the shipped rail
 * (`home-views/organizations-assets-view/main-navigation.tsx`) because that one is mounted by
 * the legacy `HomeViewFrame` too, which is the shell every customer who has not been opted
 * into `USE_UNIFIED_SHELL` still sees. Retitling it in place would ship this proposal to
 * production unflagged. The two rails therefore coexist while the flag rolls out, and the
 * legacy one retires with it (PLT-6596).
 *
 * **The titles are the proposal.** The shipped investor rail is plain runs of rows separated by
 * rules; titled collapsible sections are the corporate rail's grammar today, and
 * `design/40-shell-and-pages.md` recorded both without picking one. Moving them here is the
 * decision that document now carries, on the prototypes' argument: a rail that fuses chat
 * history into product navigation is no longer one list a user reads top to bottom, so its
 * groups need names to scan for, and a Chats group that grows without bound has to be able to
 * collapse. Row content and grouping follow `views/internal/proto/unified-shell.fixtures.ts`,
 * except in the Chats group, which PLT-6766 moved on from: the prototype's fixtures still carry
 * a `New chat` row, and this rail carries the header action that replaced it.
 *
 * **The Chats group runs on the shipped chat-history API** (PLT-6529): the user's three most
 * recent conversations, then `All chats`, under a header carrying the `+` that starts a new one.
 * Every part of it is the `/chat` history sidebar's own — the same list query, the same title
 * helper, the same actions, taken from `chat/history/` rather than reimplemented here. That is
 * deliberate and is what answers the "the two must agree on ordering and titles" question the
 * issue raised: sharing the source makes them agree by construction, where two implementations
 * would only agree until one of them was edited.
 *
 * Conversation rows offer deletion on hover or keyboard focus. The full list owns bulk deletion.
 *
 * **No Favorites row for a viewer with chat.** The favorites list moved to the launcher (PLT-6702).
 * A viewer without chat has no launcher, so for them the row sits last under My assets instead
 * (PLT-6739). The rail's one utility, the "Use in Claude or ChatGPT" entry, is not a row of this
 * list: `UnifiedShellFrame` pins it to the rail's foot, under this component and the company rail
 * alike (PLT-8002).
 */
export function UnifiedInvestorNavigation() {
  const location = useLocation();
  const { clientType } = useFrontPageConfig();
  const isTeam = useIsTeam();
  const hasSelfServeAccess = useHasSelfServeAccess();
  // The organisation the selector above the rail has picked, which is what the Custom companies
  // page renders against. The lock has to be read off the same organisation, or a customer who
  // switches to a second organisation is told it cannot create companies while the page can.
  const { selectedOrganization } = useSelectedOrganization();
  const { hasPermission: orgHasCreateCompaniesPermission } = useOrganizationHasCreateCompaniesPermission(
    selectedOrganization?.id,
  );
  const canSeeCustomCompanies = isTeam || orgHasCreateCompaniesPermission;
  // The gate every other chat entry point applies, taken from the one hook that owns it, so the
  // group never offers a row that lands on "Chat is not enabled". It covers the `USE_AI_CHAT_WEB`
  // privilege *and* PLT-5235's rule that a multi-company DMA consultant gets no chat entry point
  // off a client company — the investor home routes are never inside one, so for those users the
  // group is hidden here rather than showing rows their next turn would be refused for. Team
  // users are granted the privilege by its own short-circuit. `'public'` is the entry-point hint
  // that sends the `/chat` logo back to `/` rather than `/dashboard`.
  const { hidden: chatHidden } = useAskAiEntryPoint('public');
  // `chatHidden` is also true while the chat privilege is loading, which is right for the Chats
  // group but would flash the Favorites row in and out for a chat user on a hard reload.
  const { loading: chatPrivilegeLoading } = useHasResourcelessPrivilege(ResourcelessPermissionId.USE_AI_CHAT_WEB);

  const assetRows: RailRow[] = [
    {
      // The assets table's own route, which the launcher at `/` does not repeat (PLT-6533). The
      // Home row above is what points at the launcher. An exact match rather than
      // `getAriaCurrent`, so the row lights on the table itself and on nothing below it.
      to: FUNDS_AND_PORTFOLIOS_PATH,
      label: 'Funds and portfolios',
      icon: BagSuitcaseIcon,
      ariaCurrent: location.pathname === FUNDS_AND_PORTFOLIOS_PATH ? 'page' : undefined,
    },
    {
      to: CUSTOM_COMPANIES_PATH,
      label: 'Custom companies',
      icon: BuildingOfficeIcon,
      ariaCurrent: getAriaCurrent(CUSTOM_COMPANIES_PATH, location.pathname),
      // Always listed, locked with a tooltip when the organisation cannot create companies: the
      // row is how a customer learns the capability exists and who to ask for it.
      isDisabled: !canSeeCustomCompanies,
      tooltipText: canSeeCustomCompanies ? undefined : CUSTOM_COMPANIES_LOCKED_TOOLTIP,
    },
    ...(showSharedAssets(clientType)
      ? [
          {
            to: MANAGE_ACCESS_PATH,
            label: sharedAssetsLabel(hasSelfServeAccess),
            icon: OfficeWorkerStreamlineCore,
            ariaCurrent: getAriaCurrent(MANAGE_ACCESS_PATH, location.pathname),
          },
        ]
      : []),
    // The favorites list lives on the launcher (PLT-6702), which a viewer without chat never gets:
    // `/` sends them to Funds and portfolios instead (PLT-6739). Without this row they could star
    // an entity on its profile and have no way back to the list.
    ...(chatHidden && !chatPrivilegeLoading
      ? [
          {
            to: FAVORITES_PATH,
            label: 'Favorites',
            icon: Star1StreamlineCore,
            ariaCurrent: getAriaCurrent(FAVORITES_PATH, location.pathname),
          },
        ]
      : []),
  ];

  const databaseRows: RailRow[] = [
    {
      to: SCREEN_COMPANIES_PATH,
      label: 'Screen companies',
      icon: ZoomDocumentIcon,
      ariaCurrent: getAriaCurrent(SCREEN_COMPANIES_PATH, location.pathname),
    },
    {
      to: SCREEN_FUNDS_PATH,
      label: 'Screen funds',
      icon: SearchDollarIcon,
      ariaCurrent: getAriaCurrent(SCREEN_FUNDS_PATH, location.pathname),
    },
  ];

  return (
    <StyledNavList aria-label="Investor navigation" className={railClassName}>
      {/* The shipped selector, which hides itself below two organisations — that stays the rule
          (design/40-shell-and-pages.md, "Sidebar"), so a one-organisation rail opens on the Home
          row. */}
      <TopLevelItemWrap>
        <OrganizationSelectorSidebar />
      </TopLevelItemWrap>
      {!chatHidden && <HomeRailRow currentPath={location.pathname} />}
      {/* Chats leads the rail. This shell's proposition is that a conversation is where work
          starts, so the group a reader reaches for first sits nearest the top rather than under
          two groups of destinations (PLT-6696). It is hidden entirely for a viewer with no chat
          entry point, and the rail then opens on My assets. */}
      {!chatHidden && <ChatsRailSection />}
      <RailSection sectionKey="assets" title="My assets" rows={assetRows} />
      <RailSection sectionKey="database" title="Database" rows={databaseRows} />
      <OrganizationRailRow />
    </StyledNavList>
  );
}

/**
 * The Home row: the rail's first row, and the one thing in it that points at the launcher (`/`).
 *
 * A top-level `NavList.Item` rather than a `RailRowItem`, matching the corporate rail's Home
 * (`dma-v2-view/dashboard/main-navigation.tsx`) rather than this file's own row helper. The
 * helper wraps every row in `SubNavItemWrapper`, whose `:first-child` rule adds `sm` on top of
 * the `lg` the section wrapper already carries, and the selector box is specified as `lg` above
 * the first nav row (design/40-shell-and-pages.md, "Sidebar"). The wrapper gives a top-level
 * anchor the same `navRowBox` a sub-row gets, so the pill and the focus ring trace one shape.
 *
 * **Rendered only where the launcher is.** Without a chat entry point `/` falls back to the
 * assets table, which *Funds and portfolios* already leads to, and two rows onto one page with
 * only one of them lit is worse than no Home row. That is the same condition the Chats group
 * carries, so the two appear and disappear together.
 *
 * The current test is an equality rather than `getAriaCurrent`, whose `includes` would light this
 * row on every route in the shell: every path contains '/'.
 */
function HomeRailRow({ currentPath }: { currentPath: string }) {
  return (
    <TopLevelItemWrap>
      <NavList.Item to="/" as={LinkWithSearch} aria-current={currentPath === '/' ? 'page' : undefined}>
        <NavList.LeadingVisual>
          <HomeChimney width={20} height={20} />
        </NavList.LeadingVisual>
        Home
      </NavList.Item>
    </TopLevelItemWrap>
  );
}

/**
 * Where the sections' collapse state is remembered. Its own key, not the DMA rail's
 * `dma-v2.nav-section-open`: both rails have a section a user could reasonably collapse in one
 * product and expect open in the other, and the two would otherwise share one `chats`.
 */
const INVESTOR_NAV_COLLAPSE_STORAGE_KEY = 'unified-shell.investor-nav-section-open';

/**
 * The copy the locked Custom companies row carries, held here rather than shared with the
 * shipped rail's identical string. The shipped rail is deleted when the flag finishes rolling
 * out (PLT-6596), so a constant shared between a file and its replacement would be churn with a
 * short life — but the two are the same sentence and a change to one belongs in both while both
 * ship.
 */
const CUSTOM_COMPANIES_LOCKED_TOOLTIP =
  'Analyze any private or public company not in Upright’s database by entering the company URL and basic information. Contact your Upright customer representative to unlock access.';

/**
 * How many conversations the Chats group lists before `All chats` takes over.
 *
 * Three, following the prototype (`views/internal/proto/unified-shell.fixtures.ts`). A cap is
 * the point: conversations accumulate without bound, and an uncapped group would push the
 * product rows it sits under off the bottom of the rail. Three is enough to make the group
 * useful for "get back to what I was just doing", which is the job the full list on `/chat`
 * does badly.
 */
const RAIL_CONVERSATION_LIMIT = 3;

export interface RailRow {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  ariaCurrent?: 'page' | undefined;
  isDisabled?: boolean;
  tooltipText?: string | undefined;
  /**
   * React key, for rows whose `to` is not unique. Every row in the Chats group points at
   * `/chat` and is told apart by `additionalParams`, so those carry their own key (PLT-6529).
   * Everywhere else the destination is the identity.
   */
  rowKey?: string;
  /** Query params appended to `to` — see `SubNavItem`, which explains why not in `to`. */
  additionalParams?: Record<string, string | null | undefined>;
  /** Ran on the row's click before the router navigates — see `SubNavItem`. */
  onClick?: (event: ReactMouseEvent<HTMLElement>) => void;
  trailingAction?: ReactNode;
}

/**
 * The Chats group: the most recent conversations, then `All chats`, under a header carrying the
 * `+` that starts a new one.
 *
 * Its own component so the conversation list is only *queried* when the group is going to
 * render. `useChatConversationList` is an unconditional `useQuery`, so calling it up in the rail
 * body would put a `GET /chat/conversations` on every investor page load, including for the
 * users the group is hidden from.
 *
 * **Neither action is reimplemented here.** `startNewChat` is the sidebar's, from
 * `chat/history/chat-history-actions.ts`; the labels come from `conversationDisplayTitle`; and
 * `useChatConversationListSync` is the optimistic insert and the post-turn refresh that used to
 * live in the sidebar's body. This group is the conversation list in the one shell that mounts
 * no sidebar (PLT-6610), so without that hook a conversation the user had just started would be
 * missing from the only list on screen.
 *
 * **Starting a chat is the header's `+`, not a row of the list** (PLT-6766). A row that was
 * always first, always there and never a conversation read as one more destination in a group
 * whose other rows are places to go back to, and it pointed at a landing page that repeated the
 * launcher at `/`. As a header action it is one glyph, and the group under it is conversations
 * and nothing else. *Decided* (Janne, PLT-6766). **Searching them is the magnifier beside it**,
 * which opens the Chats page with its search field focused (PLT-7658, decided by Roni): the group
 * shows three conversations, and the search is how a reader gets to any of the others.
 */
export function ChatsRailSection() {
  const [pendingDelete, setPendingDelete] = useState<ChatConversationListItem | null>(null);
  const location = useLocation();
  const runtime = useAssistantRuntime();
  // The company scoping survives `New chat` only for consultants — see
  // `resetChatLoggingStateKeepingCompany`.
  const isConsultant = isDmaConsultantUser(useGetLoggedInUser());
  const { data, isError } = useChatConversationList();
  const activeConversationId = useActiveConversationId();

  useChatConversationListSync();

  // `data` is undefined while the query is in flight and on failure, so a group that has not
  // loaded is quietly its header alone. That matches the sidebar, which also declines to draw
  // skeleton rows: they would flicker in beside product rows that are already painted.
  const conversations = data?.slice(0, RAIL_CONVERSATION_LIMIT) ?? [];

  // Which conversation is loaded is runtime state rather than something the pathname alone can
  // be read for: a first turn sent from an empty thread becomes a conversation without a
  // navigation. So the highlight is the runtime's id, and the pathname only says whether the
  // user is looking at the chat at all — otherwise a conversation row would stay lit while the
  // user reads the assets table.
  const onChatRoute = isChatPath(location.pathname);

  const allChats: RailRow = {
    rowKey: 'all-chats',
    to: ALL_CHATS_PATH,
    label: 'All chats',
    icon: HistoryIcon,
    ariaCurrent: location.pathname === ALL_CHATS_PATH ? 'page' : undefined,
  };

  const rows: RailRow[] = [
    ...conversations.map(
      (item): RailRow => ({
        rowKey: `conversation-${item.id}`,
        // The conversation's own URL, which the chat view reads and rehydrates from (PLT-6610).
        // A path rather than a click that seeds the runtime from here: seeding before the view
        // mounts would race its own thread handling, and a real destination keeps the row a real
        // link, so cmd-click, middle-click, a reload and a login round-trip all behave. The URL
        // is per-user, not shareable: the detail endpoint answers only to the conversation's
        // owner.
        to: chatConversationPath(item.id),
        label: conversationDisplayTitle(item),
        icon: AiSparklesIcon,
        ariaCurrent: onChatRoute && item.id === activeConversationId ? 'page' : undefined,
        trailingAction: (
          <NavList.TrailingAction
            className={chatDeleteClassName}
            icon={DeleteSteamlineCore}
            label={`Delete ${conversationDisplayTitle(item)}`}
            onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              event.stopPropagation();
              setPendingDelete(item);
            }}
          />
        ),
      }),
    ),
    // `All chats` appears once there is a list worth going to see, and on a failed load, where
    // the full list is the surface that can actually report the error and let the user retry. Not
    // while the query is in flight, and not for a user with no conversations at all: a row
    // leading to an empty list is a dead end.
    ...(isError || conversations.length > 0 ? [allChats] : []),
  ];

  return (
    <>
      <RailSection
        sectionKey="chats"
        title="Chats"
        rows={rows}
        action={
          <NavSectionAction>
            {/*
             * `isNonFocusableTarget` despite the target being a perfectly focusable anchor: the
             * prop is what wraps the child in react-aria's `Focusable`, and without it the
             * trigger props reach only react-aria's own components, so a plain link never opens
             * the bubble. On an anchor the wrapper's `tabIndex: 0` changes nothing.
             */}
            <Tooltip text="Search chats" placement="right" isNonFocusableTarget>
              {/*
               * The Chats page with its search field focused (PLT-7658). The page's own search is
               * kept when the page is already open; arriving from anywhere else drops a `q` the
               * current page may use for something of its own.
               */}
              <LinkWithSearch
                to={ALL_CHATS_PATH}
                state={{ [FOCUS_CHAT_SEARCH_STATE]: true }}
                paramKeysToClear={location.pathname === ALL_CHATS_PATH ? undefined : [CHAT_SEARCH_PARAM]}
                aria-label="Search chats"
              >
                <InterfaceSearch width={16} height={16} aria-hidden="true" />
              </LinkWithSearch>
            </Tooltip>
            <Tooltip text="New chat" placement="right" isNonFocusableTarget>
              {/*
               * A real link, so cmd-click, middle-click and a reload all behave, and the reset
               * happens in the click the way the row's did.
               */}
              <LinkWithSearch
                to={CHAT_PATH}
                aria-label="New chat"
                onClick={event => {
                  // Leave modifier and non-primary clicks alone. They open a new tab, and the
                  // runtime is shared app-wide (PLT-4625), so resetting the thread here would
                  // discard a conversation in the tab the user is still looking at.
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                  startNewChat({ runtime, isConsultant });
                }}
              >
                <Plus width={16} height={16} aria-hidden="true" />
              </LinkWithSearch>
            </Tooltip>
          </NavSectionAction>
        }
        // `data` is undefined while the query is in flight and on failure, so the sentence waits
        // for an answer rather than flashing under a group that is about to fill. A failed load
        // keeps the `All chats` row instead, which is the surface that can report the error.
        emptyMessage={data !== undefined && data.length === 0 ? 'No chats yet' : undefined}
      />
      {pendingDelete !== null && (
        <ChatDeleteDialog
          conversation={pendingDelete}
          onClose={() => {
            setPendingDelete(null);
          }}
        />
      )}
    </>
  );
}

/**
 * One titled group: the header row that **is** the toggle button, and the rows under it.
 *
 * `NavList.Item` with a `SubNav` is what makes the header a button with `aria-expanded` and a
 * trailing chevron, so the whole row is the hit target rather than a static heading beside a
 * clickable glyph (design/40-shell-and-pages.md, "The section header and the collapse control
 * are one row").
 *
 * `action` is one optional control at the far end of that header row, and it is rendered
 * *outside* the `NavList.Item` because the header row is a `<button>` and nothing interactive
 * may go inside one. `NavSectionAction` positions it over the row from there and says why
 * Primer's own trailing-action slot cannot be used.
 *
 * `emptyMessage` is the sentence the group shows in place of its rows when it has none. A
 * caller that has no such state omits it, and the group then simply collapses to its header.
 */
export function RailSection({
  sectionKey,
  title,
  rows,
  action,
  emptyMessage,
}: {
  sectionKey: string;
  title: string;
  rows: RailRow[];
  action?: ReactNode;
  emptyMessage?: string;
}) {
  const { ref, onSectionClick, defaultOpen } = useNavSectionCollapse({
    storageKey: INVESTOR_NAV_COLLAPSE_STORAGE_KEY,
    sectionKey,
    // Expanded on a first visit, where the DMA rail opens collapsed. The rows are what this shell
    // offers, and there are few enough of them to fit; a rail that opens as three shut twisties
    // makes a reader open every one to find out what the product does. A viewer who collapses a
    // group still gets their own choice back on the next load.
    defaultOpen: true,
  });

  return (
    <TopLevelItemWrap ref={ref} onClick={onSectionClick}>
      <NavList.Item defaultOpen={defaultOpen}>
        <NavSectionTitle>{title}</NavSectionTitle>
        <NavList.SubNav>
          {rows.map(row => (
            <RailRowItem key={row.rowKey ?? row.to} row={row} />
          ))}
          {rows.length === 0 && emptyMessage !== undefined && (
            <NavSectionEmptyMessage>{emptyMessage}</NavSectionEmptyMessage>
          )}
        </NavList.SubNav>
      </NavList.Item>
      {action}
    </TopLevelItemWrap>
  );
}

/**
 * A row, on the one shared `SubNavItem`, which hides Primer's indent spacer so the rows keep one
 * straight icon column under a title that sits `sm` outdented from it (PLT-6226).
 */
function RailRowItem({ row }: { row: RailRow }) {
  return (
    <SubNavItem
      to={row.to}
      additionalParams={row.additionalParams}
      ariaCurrent={row.ariaCurrent}
      icon={asNavIcon(row.icon)}
      disabled={row.isDisabled}
      tooltipText={row.tooltipText}
      onClick={row.onClick}
      trailingAction={row.trailingAction}
    >
      {row.label}
    </SubNavItem>
  );
}

/**
 * The icon families are typed as SVG components and the nav primitive types its icon slot as
 * the two size props it passes. `ComponentType` is invariant in its props through `propTypes`,
 * so the SVG shape the icon modules export is rejected where the size-only one is asked for.
 * Every shipped nav file casts at each call site; this is that cast, named once.
 */
function asNavIcon(icon: ComponentType<SVGProps<SVGSVGElement>>): ComponentType<{ width?: number; height?: number }> {
  return icon as ComponentType<{ width?: number; height?: number }>;
}
